import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import { Button } from '@material-ui/core'
import DefaultModalContent from 'components/shared/DefaultModal/DefaultModalContent'
import OrganizationForm from '../../components/OrganizationForm'
import ErrorMessage from '../../components/ErrorMessage'
import useCredentials from '../../hooks/useCredentials'
import { OrganizationDTO } from '../../dtos/OrganizationDTO'
import CircularSpinner from '../../components/CircularSpinner'
import EditOrganizationLayout from '../../layouts/EditOrganizationLayout'
import Navbar from '../../components/Navbar'

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    '& p': {
      textAlign: 'center',
      color: theme.palette.neutrals[600],
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',

    '&>button': {
      width: '100%',
      textTransform: 'uppercase',
    },
  },
  title: {
    marginTop: '77px',
    textAlign: 'center',
    fontSize: '32px',
    fontWeight: 600,
  },
  whiteLabelContainer: {
    display: 'flex',
    padding: theme.spacing(3),
    alignItems: 'flex-end',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),
    backgroundColor: theme.palette.primary[100],
  },
  whiteLabelInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',

    '& > h3': {
      fontSize: 24,
      fontWeight: 700,
      color: theme.palette.neutrals[600],
    },
    '& > p': { fontSize: 16, fontWeight: 400, color: theme.palette.neutrals[600] },
  },
}))

const EditOrganization: React.FC = () => {
  const { credentials } = useCredentials()
  const params = useParams<{ id: string }>()
  const classes = useStyles()

  const [whitelabelModalState, setWhitelabelModalState] = useState<{
    white_label: boolean
    id: string
  } | null>(null)

  const onWhiteLabelChange = async (id: string) => {
    try {
      await axios.put(`/api/panel/organizations/${id}/toggle_white_label`, {}, { headers: credentials })
      loadOrganization()
    } catch (e) {
      console.error(e.message)
    }
  }

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<OrganizationDTO>({
    name: '',
    subdomain: '',
    description: '',
    website: '',
    portal_type: '',
    portal_status: '',
    active_campaign_enabled: false,
    active_campaign_insider_enabled: false,
    paid_by_organization: false,
    marketplace_offers_only: false,
    hide_sub_accounts_tab: false,
    hide_payments_tab: false,
    external_url: '',
  })
  const [isWhiteLabel, setIsWhiteLabel] = useState(false)
  const [subDomain, setSubDomain] = useState('')
  const [isFormEdited, setIsFormEdited] = useState(false)
  const [externalUrl, setExternalUrl] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [loadDataError, setLoadDataError] = useState('')

  const loadOrganization = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`/api/panel/organizations/${params.id}/edit`, {
        headers: credentials,
      })
      setFormData(res.data)
      setIsWhiteLabel(res.data.white_label)
      setSubDomain(res.data.subdomain)
      setExternalUrl(res.data.external_url)
    } catch (e) {
      setLoadDataError(e.message)
    } finally {
      setLoading(false)
    }
  }, [credentials, params.id])

  useEffect(() => {
    loadOrganization()
  }, [loadOrganization])

  const handleChange = (newData: any, shouldSkipDirty = false) => {
    if (!shouldSkipDirty) {
      setIsFormEdited(true)
    }
    setFormData(newData)
  }

  const handleSubmit = async (data) => {
    try {
      await axios.put(`/api/panel/organizations/${params.id}`, data, {
        headers: credentials,
      })
      setIsFormEdited(false)
      setExternalUrl(data.external_url)
    } catch (e) {
      setValidationErrors(e.response?.data)
    }
  }

  const handlePublish = () => {
    const fd = new FormData()
    if (formData.name) fd.append('name', formData.name)
    if (formData.subdomain) fd.append('subdomain', formData.subdomain)
    if (formData.website) fd.append('website', formData.website)
    if (formData.logo) fd.append('logo', formData.logo)
    if (formData.login_image) fd.append('login_image', formData.login_image)
    if (formData.chargify_subdomain) fd.append('chargify_subdomain', formData.chargify_subdomain)
    if (formData.description) fd.append('description', formData.description)
    if (formData.portal_type) fd.append('portal_type', formData.portal_type)
    if (formData.portal_status) fd.append('portal_status', formData.portal_status)
    if (formData.external_url !== undefined && formData.external_url !== null) {
      fd.append('external_url', formData.external_url as string)
    }
    fd.append('paid_by_organization', String(formData.paid_by_organization))
    fd.append('marketplace_offers_only', String(formData.marketplace_offers_only))
    if (formData.main_subscription_id) fd.append('main_subscription_id', formData.main_subscription_id)
    fd.append('active_campaign_enabled', String(formData.active_campaign_enabled === true))
    fd.append('active_campaign_insider_enabled', String(formData.active_campaign_insider_enabled === true))
    fd.append('hide_sub_accounts_tab', String(formData.hide_sub_accounts_tab === true))
    fd.append('hide_payments_tab', String(formData.hide_payments_tab === true))

    handleSubmit(fd)
  }

  return (
    <div>
      <div className='hero-head'>
        <Navbar />
      </div>
      {loadDataError && <ErrorMessage text={loadDataError} />}
      {loading ? (
        <CircularSpinner className='is-center' />
      ) : (
        <EditOrganizationLayout
          organizationId={params.id}
          reloadOrganization={loadOrganization}
          isWhiteLabel={isWhiteLabel}
          subDomain={subDomain}
          handlePublish={handlePublish}
          isFormEdited={isFormEdited}
          isWhiteLabelPage={false}
          customUrl={externalUrl}
        >
          <div className={classes.content}>
            <OrganizationForm
              title='EXEC Portal info'
              formData={formData}
              onChange={handleChange}
              onSubmit={handleSubmit}
              errors={validationErrors}
              isEdit
            />
            <div className={classes.whiteLabelContainer}>
              <div className={classes.whiteLabelInfo}>
                <h3>White label</h3>
                <p>Enable white label to customize the portal appearance.</p>
              </div>
              <Button
                variant='outlined'
                onClick={() => {
                  setWhitelabelModalState({ white_label: isWhiteLabel, id: params.id })
                }}
              >
                {isWhiteLabel ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          </div>
          <DefaultModalContent
            isModalOpen={!!whitelabelModalState}
            toggleModal={() => setWhitelabelModalState(null)}
            header={
              <h4 className={classes.title}>
                {whitelabelModalState?.white_label ? 'Deactivate White Label?' : 'Activate White Label?'}
              </h4>
            }
            content={
              <div className={classes.modalContent}>
                <p>
                  {whitelabelModalState?.white_label
                    ? 'Are you sure you want to deactivate the White Label feature?'
                    : 'Are you sure you want to activate the White Label feature?'}
                </p>
                <p />
                <p>
                  {whitelabelModalState?.white_label
                    ? 'If you proceed, you will lose access to all White Label customization options and related features.'
                    : ''}
                </p>
              </div>
            }
            actions={
              <div className={classes.actions}>
                <Button variant='contained' color='primary' onClick={() => setWhitelabelModalState(null)}>
                  Cancel
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    onWhiteLabelChange(whitelabelModalState?.id || '')
                    setWhitelabelModalState(null)
                  }}
                >
                  {whitelabelModalState?.white_label ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            }
          />
        </EditOrganizationLayout>
      )}
    </div>
  )
}

export default EditOrganization
